import type {
  FilteredProduct,
  GroupParentAttributeOptionSet,
  Product,
} from "@/components/configurator/global-option/types";
import { toast } from "sonner";
import { applyFilters } from "@/extensions/hooks";
import type { FilterMap } from "@/extensions/hook-map";
import type { GlobalOptions } from "@/utils/form-data-manager";

type ProductsContext = FilterMap["body.products"]["context"];

/**
 * Runs the "body.products" rules over offered products. Generic so each caller keeps its own
 * item type (FilteredProduct, Global Search choices): rules only remove products and keep the
 * other fields of every item.
 */
export function filterProductsByRules<T extends { products: Product[] }>(
  productGroups: T[],
  { optionSet, groupId, ...context }: Omit<ProductsContext, "groupName">,
): T[] {
  return applyFilters("body.products", productGroups as unknown as FilteredProduct[], {
    ...context,
    optionSet,
    groupId,
    groupName: optionSet.get(groupId)?.groupName ?? "",
  }) as unknown as T[];
}

/**
 * Picks among `picks` whose option the "attribute.options" rules disable while `selectedPicks`
 * are selected on a body, e.g. a body's own picks that newly applied global picks now block.
 */
export function findRuleBlockedPicks(
  picks: GlobalOptions[],
  selectedPicks: GlobalOptions[],
  optionSet: GroupParentAttributeOptionSet,
): GlobalOptions[] {
  return picks.filter((pick) => {
    if (!pick.value) return false;
    const group = optionSet.get(String(pick.group_key));
    const parents = [...(group?.parents ?? [])];
    const [parentName = "", parent] =
      parents.find(([name]) => name === pick.parent_name) ??
      // mapDukeActiveStructure saves a "default" parent as "": use the parent holding the attribute
      parents.find(([, { child }]) => child.some((item) => item.attributeCode === pick.key)) ??
      [];
    const attribute = parent?.child.find((child) => child.attributeCode === pick.key);
    if (!group || !attribute) return false;

    const options = applyFilters(
      "attribute.options",
      // Judge the rules alone, not the dropdown's current enabled/disabled state
      attribute.labelOptions.options.map((option) => ({ ...option, isDisabled: false })),
      {
        scope: "body",
        picks: selectedPicks,
        groupName: group.groupName,
        parentName,
        attributeCode: pick.key,
        optionSet,
      },
    );
    return options.some(
      (option) => String(option.value) === String(pick.value) && option.isDisabled,
    );
  });
}

/**
 * Groups the picks of `groupId` (the group being applied) make the "attribute.options" rules block,
 * each judged against the picks of every other group, e.g. a Cutting Board once two Tray Slide
 * groups take both sides. `groupId` is never returned: it wins over the others. A conflict between
 * two other groups that holds without `groupId` (e.g. a quote saved before the rule) is left alone.
 */
export function findRuleBlockedGroupIds(
  picks: GlobalOptions[],
  groupId: string,
  optionSet: GroupParentAttributeOptionSet,
): string[] {
  const otherGroupIds = new Set(
    picks.map((pick) => String(pick.group_key)).filter((id) => id !== String(groupId)),
  );
  return [...otherGroupIds].filter((id) => {
    const groupPicks = picks.filter((pick) => String(pick.group_key) === id);
    const otherPicks = picks.filter((pick) => String(pick.group_key) !== id);
    const isBlockedBy = (selectedPicks: GlobalOptions[]) =>
      findRuleBlockedPicks(groupPicks, selectedPicks, optionSet).length > 0;
    return (
      isBlockedBy(otherPicks) &&
      !isBlockedBy(otherPicks.filter((pick) => String(pick.group_key) !== String(groupId)))
    );
  });
}

// One toast after an apply, naming every group the rules cleared (globally or on any body)
export function notifyRuleClearedGroups(
  clearedGroupIds: string[],
  groupId: string,
  optionSet: GroupParentAttributeOptionSet,
) {
  if (!clearedGroupIds.length) return;
  const groupName = (id: string) => optionSet.get(id)?.groupName ?? id;
  toast.warning(
    `${clearedGroupIds.map(groupName).join(", ")} removed, it conflicts with the ${groupName(groupId)} selection.`,
    { id: `rule-cleared:${groupId}`, duration: 3000 },
  );
}
